import React from "react";
import { Link } from "react-router-dom";
import { Linkedin, Instagram, Twitter, Facebook } from "lucide-react";
import { PHONE_DISPLAY, EMAIL, SOCIALS, CALENDLY_URL } from "@/lib/api";

const SOCIAL_LINKS = [
  { href: SOCIALS.linkedin, label: "LinkedIn", Icon: Linkedin },
  { href: SOCIALS.instagram, label: "Instagram", Icon: Instagram },
  { href: SOCIALS.twitter, label: "X (Twitter)", Icon: Twitter },
  { href: SOCIALS.facebook, label: "Facebook", Icon: Facebook },
];

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-forest-600 text-cream-100 mt-24">
      <div className="max-w-[1320px] mx-auto px-5 lg:px-10 pt-8 pb-6">
        {/* TOP ROW — Big aidify logo on its own */}
        <div className="border-b border-cream-100/15 pb-4">
          <img
            src="https://customer-assets.emergentagent.com/job_design-to-live-15/artifacts/czg66aif_image.png"
            alt="aidify logo"
            className="h-72 md:h-80 lg:h-96 w-auto object-contain -ml-10 md:-ml-14 -my-12 md:-my-16"
          />
        </div>

        {/* MAIN ROW — Description + Services + Contact (all top-aligned) */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 pt-10">
          <div className="md:col-span-5">
            <p className="max-w-md text-cream-100/70 text-sm leading-relaxed">
              Global talent with local accountability. Vetted offshore specialists, managed by US-based operators with 25+ years of experience.
            </p>

            <div className="mt-7 flex flex-wrap items-center gap-3" data-testid="footer-socials">
              {SOCIAL_LINKS.map(({ href, label, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={label}
                  data-testid={`social-${label.toLowerCase().split(" ")[0]}`}
                  className="w-11 h-11 rounded-full border border-cream-100/20 flex items-center justify-center text-cream-100/80 hover:bg-sage-200 hover:text-forest-600 hover:border-sage-200 transition-all duration-300 hover:-translate-y-1"
                >
                  <Icon size={18} />
                </a>
              ))}
            </div>
          </div>

          <div className="md:col-span-3">
            <h4 className="text-cream-100/60 text-xs uppercase tracking-[0.2em] mb-4">Services</h4>
            <ul className="space-y-3 text-sm">
              <li><Link to="/bookkeeping-services" className="hover:text-sage-200 transition-colors">Bookkeeping Services</Link></li>
              <li><Link to="/virtual-assistant-services" className="hover:text-sage-200 transition-colors">Virtual Assistance</Link></li>
              <li><Link to="/marketing-assistance" className="hover:text-sage-200 transition-colors">Marketing Assistance</Link></li>
              <li><Link to="/customer-support" className="hover:text-sage-200 transition-colors">Customer Support</Link></li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <h4 className="text-cream-100/60 text-xs uppercase tracking-[0.2em] mb-4">Contact</h4>
            <ul className="space-y-3 text-sm">
              <li><a href="tel:+16465951666" className="hover:text-sage-200">{PHONE_DISPLAY}</a></li>
              <li><a href={`mailto:${EMAIL}`} className="hover:text-sage-200">{EMAIL}</a></li>
              <li className="pt-2"><a href={CALENDLY_URL} target="_blank" rel="noreferrer" className="btn-on-dark text-sm">Book a Call →</a></li>
            </ul>
          </div>
        </div>

        {/* Other Companies */}
        <div className="mt-14 pt-10 border-t border-cream-100/15">
          <p className="text-cream-100/60 text-xs uppercase tracking-[0.2em] mb-6">Other Companies</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <a
              href="https://ansrpo.com"
              target="_blank"
              rel="noreferrer"
              data-testid="other-co-ans"
              className="aidify-card-dark border-cream-100/15 hover:border-sage-200 p-6 flex items-center justify-center h-52 transition-all duration-300"
              title="ANS RPO Solutions"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_design-to-live-15/artifacts/ohnrydra_ANS_rpo_logo.png"
                alt="ANS RPO Solutions"
                className="h-36 w-auto object-contain"
              />
            </a>
            <a
              href="https://hireecom.com"
              target="_blank"
              rel="noreferrer"
              data-testid="other-co-hireecom"
              className="aidify-card-dark border-cream-100/15 hover:border-sage-200 p-6 flex items-center justify-center h-52 transition-all duration-300"
              title="Hire eCom"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_design-to-live-15/artifacts/ozpdhasp_Hire_eCom_logo.png"
                alt="Hire eCom"
                className="h-auto w-full max-w-[300px] object-contain"
              />
            </a>
            <a
              href="https://www.outsourcingpartnersinc.com"
              target="_blank"
              rel="noreferrer"
              data-testid="other-co-outsourcing"
              className="aidify-card-dark border-cream-100/15 hover:border-sage-200 p-6 flex items-center justify-center h-52 transition-all duration-300"
              title="Outsourcing Partners Inc"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_design-to-live-15/artifacts/4pnt67np_Outsourcing_logo.png"
                alt="Outsourcing Partners Inc"
                className="h-auto w-full max-w-[300px] object-contain"
              />
            </a>
          </div>
        </div>

        <div className="mt-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-cream-100/50">
          <span>© 2026 Aidify. All rights reserved.</span>
          <span className="font-serif-italic text-base text-cream-100/70">Focus on what matters.</span>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <Link to="/our-team" className="hover:text-sage-200">Our Team</Link>
            <Link to="/contact" className="hover:text-sage-200">Contact Us</Link>
            <Link to="/privacy-policy" className="hover:text-sage-200">Privacy Policy</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
